import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Collapses a preference + OS signal into the concrete theme to render. */
export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light";
  return preference;
}

/** Applies (or removes) the `.dark` class on the document root. */
export function applyThemeClass(resolved: ResolvedTheme, root: HTMLElement): void {
  root.classList.toggle("dark", resolved === "dark");
  root.dataset["theme"] = resolved;
}

export function prefersDarkScheme(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export interface ThemeState {
  readonly preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      preference: "system",
      setPreference: (preference) => {
        set({ preference });
        applyThemeClass(resolveTheme(preference, prefersDarkScheme()), document.documentElement);
      },
      toggle: () => {
        const current = resolveTheme(get().preference, prefersDarkScheme());
        const next: ThemePreference = current === "dark" ? "light" : "dark";
        set({ preference: next });
        applyThemeClass(next, document.documentElement);
      },
    }),
    {
      name: "mailflare-theme",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ preference: state.preference }),
    },
  ),
);
